// Gerador de HTML da proposta comercial — usado tanto na impressão interna
// quanto na página pública compartilhada por link/WhatsApp.

const STATUS_LABELS = {
  draft: 'Rascunho',
  sent: 'Enviada',
  accepted: 'Aceita',
  rejected: 'Recusada',
};

export const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
export const safe = (v) => (v == null ? '' : String(v).replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c])));

export function buildProposalHTML(proposal, { company = {}, tenant = {}, defaultClauses = '', autoPrint = true } = {}) {
  const cust = proposal.customer_snapshot || {};
  const enderecoFromParts = [
    cust.logradouro || cust.address,
    cust.numero ? `nº ${cust.numero}` : '',
    cust.complemento,
    cust.bairro,
    [cust.cidade, cust.uf].filter(Boolean).join(' - '),
    cust.cep ? `CEP ${cust.cep}` : '',
  ].filter(Boolean).join(', ');

  const itemsHtml = (proposal.items || []).map((it, i) => {
    const isArea = it.measurement_type === 'area';
    const w = Number(it.width) || 0;
    const h = Number(it.height) || 0;
    const m2 = +(w * h).toFixed(4);
    const qty = isArea ? m2 : Number(it.quantity || 0);
    const sub = qty * Number(it.unit_price || 0);
    const final = sub - Number(it.discount || 0);
    const qtyLabel = isArea
      ? `${w.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} × ${h.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} m = ${m2.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} m²`
      : qty.toLocaleString('pt-BR');
    return `<tr>
      <td class="num">${i + 1}</td>
      <td>${safe(it.description)}</td>
      <td>${qtyLabel}</td>
      <td class="num">${brl(it.unit_price)}${isArea ? '/m²' : ''}</td>
      <td class="num">${brl(it.discount)}</td>
      <td class="num">${brl(final)}</td>
    </tr>`;
  }).join('');

  const proposalTotals = (proposal.items || []).reduce((acc, it) => {
    const isArea = it.measurement_type === 'area';
    const qty = isArea ? (Number(it.width) || 0) * (Number(it.height) || 0) : Number(it.quantity || 0);
    const sub = qty * Number(it.unit_price || 0);
    const disc = Number(it.discount || 0);
    acc.subtotal += sub;
    acc.discount += disc;
    acc.total += (sub - disc);
    return acc;
  }, { subtotal: 0, discount: 0, total: 0 });

  const validity = proposal.validity_days || 30;
  const deliveryDays = proposal.delivery_days || 0;
  const clauses = (proposal.contract_clauses || defaultClauses || '')
    .replace('{VALIDADE}', String(validity))
    .replace('{PRAZO_ENTREGA}', deliveryDays > 0 ? String(deliveryDays) : '____')
    .replace('{MULTA_DIARIA}', proposal.daily_penalty ? String(proposal.daily_penalty) : '____');
  const createdAt = proposal.created_at ? new Date(proposal.created_at).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR');
  const logo = tenant?.logo || company?.logo || '';
  const statusLabel = STATUS_LABELS[proposal.status] || proposal.status || '';

  return `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${safe(proposal.title || 'Proposta')}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #111; margin: 18mm; font-size: 12px; line-height: 1.4; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  h2 { font-size: 14px; margin: 14px 0 6px; text-transform: uppercase; letter-spacing: .5px; color: #555; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; padding-bottom: 12px; border-bottom: 2px solid #333; }
  .header img { max-height: 70px; max-width: 180px; object-fit: contain; }
  .meta { font-size: 11px; color: #444; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
  th { background: #f3f3f3; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .totals { width: 320px; margin-left: auto; margin-top: 6px; }
  .totals tr td { border: none; padding: 2px 6px; }
  .totals tr td:last-child { text-align: right; }
  .totals .grand { font-size: 14px; font-weight: 700; border-top: 2px solid #000; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .box { border: 1px solid #e3e3e3; border-radius: 6px; padding: 8px 10px; background: #fafafa; }
  .clauses { white-space: pre-wrap; font-size: 11px; line-height: 1.5; }
  .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 60px; }
  .signatures .line { border-top: 1px solid #000; padding-top: 4px; text-align: center; font-size: 11px; }
  .footer { margin-top: 16px; font-size: 10px; color: #888; text-align: center; }
  .actionbar { position: sticky; top: 0; background: #7c3aed; color: #fff; padding: 10px 16px; display: flex; justify-content: space-between; align-items: center; margin: -18mm -18mm 16px; border-radius: 0; }
  .actionbar button { background: #fff; color: #7c3aed; border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 13px; }
  @media print { body { margin: 12mm; } .no-print { display: none !important; } .actionbar { display: none !important; } }
  @media (max-width: 600px) { body { margin: 10px; font-size: 13px; } .grid { grid-template-columns: 1fr; } .actionbar { margin: -10px -10px 12px; } .totals { width: 100%; } }
</style></head>
<body>
  <div class="actionbar no-print">
    <span>Proposta • ${safe((company.razao_social || company.nome_fantasia || 'Empresa'))}</span>
    <button onclick="window.print()">📄 Baixar / Imprimir PDF</button>
  </div>
  <div class="header">
    <div>
      ${logo ? `<img src="${safe(logo)}" alt="Logo"/><br/>` : ''}
      <h1>${safe((company.razao_social || company.nome_fantasia || 'EMPRESA').toUpperCase())}</h1>
      <div class="meta">
        ${company.nome_fantasia && company.razao_social && company.nome_fantasia !== company.razao_social ? safe(company.nome_fantasia) + '<br/>' : ''}
        ${company.cnpj ? `CNPJ: ${safe(company.cnpj)}` : ''} ${company.inscricao_estadual ? ` | IE: ${safe(company.inscricao_estadual)}` : ''}<br/>
        ${[company.endereco || company.logradouro, company.numero ? 'nº ' + company.numero : '', company.bairro].filter(Boolean).map(safe).join(', ')}<br/>
        ${[company.cidade, company.uf].filter(Boolean).map(safe).join(' - ')} ${company.cep ? 'CEP ' + safe(company.cep) : ''}<br/>
        ${company.telefone ? 'Tel: ' + safe(company.telefone) : ''} ${company.email ? ' | ' + safe(company.email) : ''}
      </div>
    </div>
    <div style="text-align:right">
      <h2 style="margin:0">PROPOSTA COMERCIAL</h2>
      <div class="meta">Nº ${safe((proposal.id || '').slice(0, 8).toUpperCase())}</div>
      <div class="meta">Data: ${safe(createdAt)}</div>
      <div class="meta">Validade: ${validity} dias</div>
    </div>
  </div>

  <h2>${safe(proposal.title)}</h2>

  <div class="grid">
    <div class="box">
      <strong>CLIENTE</strong><br/>
      ${safe(cust.name || '—')}<br/>
      ${cust.cpf_cnpj ? `CPF/CNPJ: ${safe(cust.cpf_cnpj)}<br/>` : ''}
      ${cust.email ? `E-mail: ${safe(cust.email)}<br/>` : ''}
      ${cust.phone ? `Telefone: ${safe(cust.phone)}<br/>` : ''}
      ${enderecoFromParts ? `Endereço: ${safe(enderecoFromParts)}` : ''}
    </div>
    <div class="box">
      <strong>CONDIÇÕES</strong><br/>
      ${proposal.payment_terms ? `Pagamento: ${safe(proposal.payment_terms)}<br/>` : ''}
      Validade desta proposta: ${validity} dias<br/>
      ${deliveryDays > 0 ? `Prazo de entrega da obra: ${deliveryDays} dias (dias de chuva não contados)<br/>` : ''}
      Status: ${safe(statusLabel)}
    </div>
  </div>

  <h2>Itens</h2>
  <table>
    <thead><tr>
      <th style="width:30px">#</th>
      <th>Descrição</th>
      <th style="width:60px" class="num">Qtd</th>
      <th style="width:90px" class="num">Vl. Unit.</th>
      <th style="width:80px" class="num">Desc.</th>
      <th style="width:100px" class="num">Total</th>
    </tr></thead>
    <tbody>${itemsHtml || '<tr><td colspan="6" class="num" style="text-align:center;color:#888">Nenhum item</td></tr>'}</tbody>
  </table>

  <table class="totals">
    <tr><td>Subtotal</td><td>${brl(proposalTotals.subtotal)}</td></tr>
    ${proposalTotals.discount > 0 ? `<tr><td>Descontos</td><td>- ${brl(proposalTotals.discount)}</td></tr>` : ''}
    <tr class="grand"><td>TOTAL GERAL</td><td>${brl(proposalTotals.total)}</td></tr>
  </table>

  ${proposal.notes ? `<h2>Observações</h2><div class="box">${safe(proposal.notes)}</div>` : ''}

  <h2>Minuta de Contrato — Cláusulas e Responsabilidades</h2>
  <div class="clauses">${safe(clauses)}</div>

  <div class="signatures">
    <div class="line">${safe((company.razao_social || company.nome_fantasia || 'CONTRATADA'))}<br/><span style="font-size:10px">CONTRATADA</span></div>
    <div class="line">${safe(cust.name || 'CLIENTE')}<br/><span style="font-size:10px">CONTRATANTE</span></div>
  </div>

  <div class="footer">Documento gerado em ${new Date().toLocaleString('pt-BR')}</div>
  ${autoPrint ? `<script>window.addEventListener('load', () => setTimeout(() => window.print(), 250));</script>` : ''}
</body></html>`;
}
